import React, { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { customStyles } from "./CustomSelectStyles";
import {
    getLookupByName,
    getPatientData,
    getVisitType,
    saveAppointmentData,
} from "../utils/api";
import { useSelector } from "react-redux";
import { createOption, formattedDate, inputStyles } from "../utils/utilities";
import PaymentModal from "./PaymentModal";
import { toast } from "react-toastify";
import PaymentMobileComponent from "./PaymentMobileComponent";

const RegisteredPatientForm = ({ setShowForm, showForm }) => {
    const [patientList, setPatientList] = useState([]);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [isAppointmentSuccessful, setIsAppointmentSuccessful] =
        useState(false);
    const [genderList, setGenderList] = useState([]);
    const selectedDateISOString = useSelector(
        (state) => state.appointment.selectedDate
    );

    const selectedDate = useMemo(
        () => new Date(selectedDateISOString),
        [selectedDateISOString]
    );

    const {
        phone,
        uhid,
        selectedAppointment,
        selectedDoctor,
        selectedFacility,
        selectedSpecialization,
    } = useSelector((state) => state.appointment);
    const [showPaymentModal, setShowPaymentModal] = useState(false);

    const [formData, setFormData] = useState({
        gender: null,
        email: "",
        patient_phone: "",
        visit_type: "",
        consent: false,
    });

    const handleGetPatientData = useCallback(async () => {
        // Fetch patient data based on uhid or mobile number
        try {
            const patientData = await getPatientData(phone || uhid);
            setPatientList(
                patientData.map((patient) => ({
                    label: `${patient.properties?.first_name} ${
                        patient.properties?.middle_name ?? ""
                    } ${patient.properties?.last_name}`,
                    value: patient.properties?.uhr_id,
                    data: patient.properties,
                }))
            );
        } catch (error) {
            toast.error(error);
            // TODO: Handle error more gracefully
        }
    }, [phone, uhid]);

    useEffect(() => {
        if (phone || uhid) {
            handleGetPatientData();
        }
    }, [phone, uhid, handleGetPatientData]);

    const handlePatientSelect = (selected) => {
        let updatedFormData = { ...formData };
        Object.keys(selected.data).forEach((key) => {
            if (updatedFormData.hasOwnProperty(key)) {
                updatedFormData[key] = selected.data[key];
            }
        });
        setFormData(updatedFormData);
    };

    useEffect(() => {
        const fetchData = async () => {
            const [gender] = await Promise.all([
                getLookupByName({ lookupName: "Gender" }),
            ]);
            setGenderList(
                gender.map((item) => createOption({ label: item.value }))
            );
        };

        fetchData();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    const handleBookAppointment = async () => {
        const saveBody = {
            tenant_id: sessionStorage.getItem("tenant_name"),
            facility_name: selectedFacility.label,
            facility_id: selectedFacility.value,
            appointment_date: formattedDate(selectedDate),
            appointment_time: selectedAppointment.time,
            appointment_slot_duration: selectedAppointment.duration,
            appointment_status: "Unconfirmed",
            provider_id: selectedDoctor.value,
            provider_name: selectedDoctor.label.slice(4),
            specialization: selectedSpecialization.label,
            reason_of_visit: "",
            mode_of_appointment: "Online",
            appointment_type: selectedAppointment.type,
            visit_type: formData.visit_type,
            appointment_identification: selectedAppointment.identification,
            existing_patient: [
                {
                    patient_id: selectedPatient.value,
                    patient_name: selectedPatient.label,
                    mobile: formData.patient_phone,
                    duration: null,
                    patient_status: "Unconfirmed",
                    visit_type: "First",
                },
            ],
            new_patient: [],
        };
        try {
            const response = await saveAppointmentData(saveBody);
            if (response.statusCode === 200) {
                setIsAppointmentSuccessful(true);
                toast.success(response.message);
            } else {
                toast.error(response.message);
            }
        } catch (error) {
            toast.error(error);
        }
    };

    const handleVisitType = async () => {
        if (selectedPatient) {
            const res = await getVisitType(
                selectedDoctor.value,
                selectedPatient.value,
                selectedAppointment.date,
                selectedFacility.label,
                selectedFacility.value
            );
            if (res?.followup_type) {
                setFormData((prevState) => ({
                    ...prevState,
                    visit_type: res.followup_type,
                }));
            }
        }
    };

    const isMobile = window.innerWidth <= 768;

    return (
        <div className="patient-details-section">
            {showForm === "registered" && (
                <>
                <div className="f600-24px">Patient Details</div>
                <div className={isMobile ? "d-flex flex-column gap-2 mt-3" : "d-flex justify-content-between me-5"}>
                    <div className={isMobile ? "" : "col-3"}>
                        <label className="f500-16px">Patient Name</label>
                        <span className="text-danger" style={{ fontSize: "16px" }}>
                            *
                        </span>
                        <Select
                            options={patientList}
                            value={selectedPatient}
                            onChange={(selected) => {
                                setSelectedPatient(selected);
                                handlePatientSelect(selected);
                            }}
                            placeholder="--select patient name--"
                            styles={customStyles}
                        />
                    </div>
                    <div className={isMobile ? "" : "col-3"}>
                        <label className="f500-16px">Gender</label>
                        <span className="text-danger" style={{ fontSize: "16px" }}>
                            *
                        </span>
                        <Select
                            options={genderList}
                            value={
                                formData.gender
                                    ? {
                                          label: formData.gender,
                                          value: formData.gender,
                                      }
                                    : null
                            }
                            onChange={(selected) => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    gender: selected.label,
                                }));
                            }}
                            placeholder="--select gender--"
                            styles={customStyles}
                        />
                    </div>
                    <div className={isMobile ? "" : "col-3"}>
                        <label className="f500-16px">Email ID:</label>
                        <input
                            type="email"
                            style={inputStyles}
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                        />
                    </div>
                </div>
                <div className={isMobile ? "d-flex justify-content-center flex-column align-items-center my-5 gap-4" : "d-flex justify-content-center flex-column align-items-center mt-5 gap-4 pt-5"}>
                    <div className="d-flex align-items-center w-100 justify-content-center gap-2">
                        <div class="checkbox-wrapper-19 d-flex align-items-center ">
                            <input
                                type="checkbox"
                                name="consent"
                                id="cbtest-19"
                                checked={formData.consent}
                                onChange={(e) => {
                                    setFormData((prevState) => ({
                                        ...prevState,
                                        consent: e.target.checked,
                                    }));
                                }}
                            />
                            <label for="cbtest-19" class="check-box"></label>
                        </div>
                        <div className="f500-14px">
                            I have read & agree to the{" "}
                            <span>Terms & Conditions</span>
                        </div>
                    </div>
                    <div className={`d-flex justify-content-center w-100 ${isMobile ? "gap-3" : "gap-4"}`}>
                        <div
                            className="w-100 back-button"
                            onClick={() => setShowForm("")}
                        >
                            Back
                        </div>
                        <div
                            className="w-100 submit-button"
                            onClick={() => {
                                //handleBookAppointment();
                                if (selectedPatient === null) {
                                    toast.error(
                                        "Please select a patient to proceed"
                                    );
                                } else if (!formData.consent) {
                                    toast.error(
                                        "Please agree to the terms and conditions to proceed"
                                    );
                                } else {
                                    handleVisitType();
                                    setShowPaymentModal(true);
                                    if(isMobile){
                                        setShowForm("payment");
                                    }
                                }
                            }}
                        >
                            Submit
                        </div>
                    </div>
                </div>
                </>
            )}
            {isMobile && showForm === "payment" ? (
                <PaymentMobileComponent
                    handleBookAppointment={handleBookAppointment}
                    setShowForm={setShowForm}
                    isAppointmentSuccessful={isAppointmentSuccessful}
                />
            ) : (
                <PaymentModal
                    showPaymentModal={showPaymentModal}
                    toggleModal={() => setShowPaymentModal(false)}
                    handleBookAppointment={handleBookAppointment}
                    isAppointmentSuccessful={isAppointmentSuccessful}
                />
            )}
        </div>
    );
};

export default RegisteredPatientForm;
